import { motion } from 'motion/react';

interface SavingOverlayProps {
  current: number;
  total: number;
  status: string;
  subStep: 'capturing' | 'uploading' | 'restoring';
}

export const SavingOverlay = ({ current, total, status, subStep }: SavingOverlayProps) => {
  // More granular progress: each page is split into capture (50%) and upload (50%)
  const pageWeight = 100 / total;
  const baseProgress = ((current - 1) / total) * 100;
  const subStepProgress = subStep === 'capturing' ? pageWeight * 0.4 : subStep === 'uploading' ? pageWeight * 0.9 : pageWeight;
  const percent = Math.min(Math.round(baseProgress + subStepProgress), 99);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-md"
    >
      <div className="flex flex-col items-center gap-6 max-w-sm w-full px-8">
        <div className="relative">
          <div className="w-24 h-24 border-4 border-gray-100 rounded-full" />
          <motion.div 
            className="absolute inset-0 border-4 border-teal-500 rounded-full border-t-transparent"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-black text-teal-600 tabular-nums">{percent === 99 ? '99' : percent}%</span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 text-center">
          <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight">Saving Design</h2>
          <p className="text-[13px] font-bold text-gray-500">{status}</p>
        </div>

        <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-teal-500"
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center leading-relaxed">
          Please do not close or refresh.<br/>
          Ensuring every detail is perfectly saved.
        </p>
      </div>
    </motion.div>
  );
};
